import {
  LATEST_STORAGE_SCHEMA_VERSION,
  MINIMUM_SUPPORTED_STORAGE_SCHEMA_VERSION,
  runStorageMigrations,
  STORAGE_MIGRATIONS,
} from '../migrations';
import type { StorageMigration, StorageMigrationResult } from '../migrations';
import type { ThriveRepositories } from '../interfaces/repositories';
import {
  inheritGovernedOrigin,
  installCrossContextWatcher,
  runErasure,
  withGovernedWrites,
  type GovernedWriteAnchors,
} from '../erasure-boundary';
import { decontaminateNamespace } from './decontamination';
import {
  projectChildren,
  projectSessions,
  projectStorageMeta,
  type StorageMeta,
} from './governed-records';
import { createJsonStore, type JsonStoreOptions } from './json-store';
import { eraseFamilyData, type DatabaseFactory, type FamilyErasureResult } from './family-erasure';
import { createKeyValueStore } from './key-value-store';
import { createWriteQueue } from './write-queue';
import type { StorageBackend, StorageLike } from './key-value-store';
import { STORAGE_KEYS } from './keys';
import { createLocalProfileRepository } from './profile-repository';
import {
  createLocalLaunchIdentityRepository,
  withSerialisedIdentityWrites,
} from './launch-identity-repository';
import {
  createLocalMissionProgressRepository,
  withSerialisedWrites,
} from './mission-progress-repository';
import { createLocalProgressRepository } from './progress-repository';
import { createLocalSessionRepository } from './session-repository';
import { createLocalSkillRepository } from './skill-repository';

export * from './json-store';
export * from './key-value-store';
export * from './keys';
export * from './persistence-dto';
export * from './write-queue';
export { createLocalProfileRepository } from './profile-repository';
export {
  createLocalLaunchIdentityRepository,
  withSerialisedIdentityWrites,
} from './launch-identity-repository';
export {
  createLocalMissionProgressRepository,
  withSerialisedWrites,
} from './mission-progress-repository';
export { createLocalProgressRepository } from './progress-repository';
export { createLocalSessionRepository } from './session-repository';
export { createLocalSkillRepository } from './skill-repository';

/**
 * The cross-tab delete watcher, installed when this module is loaded (P6R14-3).
 *
 * At **module scope**, deliberately, and not from a component or even from
 * `createLocalRepositories`: a boundary that a screen can be early for is not a boundary, and round
 * 13's version lived in a passive React effect, so a delete arriving between a stage capturing its
 * epoch and its effects running was dropped on the floor. Loading the storage layer is the earliest
 * honest moment, it happens once per realm, and it is what every entry point into this app does
 * before anything renders.
 */
installCrossContextWatcher();

export interface LocalRepositories extends ThriveRepositories {
  /**
   * Live storage status — a getter, not a snapshot, because storage can stop accepting writes
   * part-way through a session and the shell has to be able to say so.
   */
  readonly backend: StorageBackend;
  /** Notifies when the backend degrades. Returns an unsubscribe function. */
  subscribeToBackend(listener: (backend: StorageBackend) => void): () => void;
  readonly migration: StorageMigrationResult;
  /**
   * A parent's delete, as physical erasure with a verification read-back (BL-003, PS6-3).
   *
   * The **operation** is exposed, never the key/value store it needs. A screen that could reach
   * the store could write to it, and the repository boundary (PRO-01) exists precisely so that no
   * screen can. See `family-erasure.ts` for what "physical" is required to mean.
   */
  eraseEverything(): Promise<FamilyErasureResult>;
}

export interface CreateLocalRepositoriesOptions {
  storage?: StorageLike | null;
  migrations?: readonly StorageMigration[];
  onWarning?: (message: string, cause?: unknown) => void;
}

/**
 * Builds the local repository bundle and brings storage up to the current schema first.
 * The app shell wires this once; nothing else constructs repositories.
 *
 * If the version write below fails — which it silently can on degraded storage — the next
 * launch simply re-runs the same migrations. That is safe precisely because migrations are
 * required to be idempotent (see `StorageMigration.migrate`).
 *
 * **Where decontamination sits, and why.** The pass runs first, from inside the key/value
 * store's construction scope: before the migration runner, before the first governed read,
 * before any repository, and therefore before any session overlay exists at all (review round 6,
 * SC-4). That position is what makes "no callback runs mid-pass" and "no overlay hides a stored
 * value" true by construction rather than by argument.
 *
 * It cannot destroy data a migration owns, because a supported *older* envelope is left
 * untouched — the same rule the read path applies. The cost of that is a record which is both
 * contaminated and awaiting an upgrade: the first pass leaves it for the migration, so the pass
 * runs a second time when the chain actually applied a step, and the record is then projected at
 * the version it was just upgraded to. On a healthy relaunch no migration applies, so there is
 * exactly one pass and it makes no writes.
 */
export function createLocalRepositories(
  options: CreateLocalRepositoriesOptions = {},
): LocalRepositories {
  const jsonOptions: JsonStoreOptions = {
    schemaVersion: LATEST_STORAGE_SCHEMA_VERSION,
    minimumSupportedVersion: MINIMUM_SUPPORTED_STORAGE_SCHEMA_VERSION,
    ...(options.onWarning === undefined ? {} : { onWarning: options.onWarning }),
  };

  const keyValueStore = createKeyValueStore({
    ...(options.storage === undefined ? {} : { storage: options.storage }),
    ...(options.onWarning === undefined ? {} : { onWarning: options.onWarning }),
    onConstructed: (store) => {
      decontaminateNamespace(store, jsonOptions);
    },
  });

  const store = createJsonStore(keyValueStore, jsonOptions);

  // Metadata is read through the envelope door, not the projected one: on a future build the
  // recorded version is by definition older than the current schema, and refusing it for being
  // old would make the migration runner think it was starting from nothing. The pass above has
  // already guaranteed that whatever comes back here is either clean or absent.
  const meta = projectStorageMeta(store.readEnvelope(STORAGE_KEYS.meta)?.data ?? null);
  const migration = runStorageMigrations(
    store,
    options.migrations ?? STORAGE_MIGRATIONS,
    meta?.schemaVersion ?? 0,
  );
  store.write<StorageMeta>(STORAGE_KEYS.meta, { schemaVersion: migration.toVersion });

  // Only when a migration actually rewrote something: records upgraded just now have never been
  // projected at their new version, and a migration is also the one thing that can introduce a
  // key the allowlist does not recognise.
  if (migration.applied.length > 0) decontaminateNamespace(keyValueStore, jsonOptions);

  /**
   * One chain for the pair of records that describe the in-flight sitting (Slice R).
   *
   * The mission snapshot and the launch identity are cleared together everywhere, so a clear of
   * either must never outrun a pending write of the other — a snapshot the guard cannot attribute
   * is a snapshot a child loses.
   */
  const sittingWrites = createWriteQueue();

  /**
   * The anchor reads the governed-write guard is predicated on (P6R15-1).
   *
   * Synchronous and physical on purpose: they are asked **inside the lock, at the commit point**, and
   * an answer that had to be awaited would reopen the very gap they exist to close. They read the
   * same governed records everything else reads, through the same projectors — no new key, and no
   * second opinion about what a session or a child is.
   */
  const anchors: GovernedWriteAnchors = {
    sessionExists: (sessionId) =>
      (store.read(STORAGE_KEYS.sessions, projectSessions) ?? []).some(
        (session) => session.sessionId === sessionId,
      ),
    childExists: (profileId) =>
      (store.read(STORAGE_KEYS.children, projectChildren) ?? []).some(
        (child) => child.id === profileId,
      ),
    /**
     * The same read, asking the question that can tell two children apart (P6R16-2).
     *
     * `createdAt` is the generation (D-056(6)) and it is already stored — nothing new is persisted
     * about anybody, and nothing new is read: the same governed record, through the same projector.
     */
    childGenerationMatches: (profileId, createdAt) =>
      (store.read(STORAGE_KEYS.children, projectChildren) ?? []).some(
        (child) => child.id === profileId && child.createdAt === createdAt,
      ),
  };

  /**
   * The bundle the whole app is handed is the **guarded** one (P6R15-2).
   *
   * Round 14 guarded the sitting and left the parent zone writing straight through, which is the
   * one-path rule with a page-shaped hole in it. Guarding at construction is what closes it for every
   * caller there is and every caller there will be: there is no unguarded bundle to hand out.
   */
  const guarded = withGovernedWrites(
    {
      profiles: createLocalProfileRepository(store),
      progress: createLocalProgressRepository(store),
      sessions: createLocalSessionRepository(store),
      skills: createLocalSkillRepository(store),
      // One chain for the one slot: the runtime and the shell write to the same instance (R2-1),
      // and since Slice R the identity beside it shares that instance too.
      missionProgress: withSerialisedWrites(
        createLocalMissionProgressRepository(store),
        sittingWrites,
      ),
      launchIdentity: withSerialisedIdentityWrites(
        createLocalLaunchIdentityRepository(store),
        sittingWrites,
      ),
    },
    anchors,
  );

  return inheritGovernedOrigin(
    {
      ...guarded,
      get backend(): StorageBackend {
        return keyValueStore.backend;
      },
      subscribeToBackend: (listener) => keyValueStore.subscribe(listener),
      migration,
      /**
       * A parent's delete, **serialized with the sitting's own writes and announced** (P6R13-1).
       *
       * Two changes from the round-12 shape, and both are about the same promise. It runs *through*
       * `sittingWrites`, so a checkpoint already in flight cannot land in the middle of the erasure
       * and be read back as a survivor — deletion is not a third path around the queue that
       * `playAgain` was made to obey (D-121(3)). And it announces itself when it is done, because the
       * bytes going away is only half of what a parent was promised: every writer still holding
       * pre-delete state has to stop being allowed to write, in this tab and in any other.
       *
       * Announced **after** the read-back, deliberately: the boundary is a fact about a delete that
       * actually happened, and `complete` is still computed from what the disk said.
       */
      eraseEverything: async () => {
        return runErasure(() =>
          sittingWrites(() =>
            eraseFamilyData(
              keyValueStore,
              typeof indexedDB === 'undefined'
                ? undefined
                : (indexedDB as unknown as DatabaseFactory),
            ),
          ),
        );
      },
    },
    guarded,
  );
}
